import React, { useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { AttachFileOutlined, CloseOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError, meApi } from '../../api';
import { translateApiError } from '../../i18n/translateApiError';
import { formatFileSize, safeAttachmentFilename } from './ProcessFormResourceValue';
import { attachmentAccept } from './processFormContract';

export interface StagedProcessAttachment {
  id: string;
  field_id: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  document_kind?: string | null;
  status: string;
}

interface Props {
  definitionId: string;
  fieldId: string;
  label: string;
  required?: boolean;
  documentKind?: string;
  accept?: string;
  /** The field's own allowed formats (`properties.allowedTypes`). */
  allowedTypes?: unknown;
  maxBytes?: number;
  value?: StagedProcessAttachment;
  onChange: (value: StagedProcessAttachment | undefined) => void;
  onBusyChange: (busy: boolean) => void;
  onErrorChange: (hasError: boolean) => void;
}

const BACKEND_MAX_BYTES = 10 * 1024 * 1024;
// No INVOICE_ACCEPT any more. It was `application/pdf,.pdf`, so the picker refused a PHOTO of an
// invoice even though the upload endpoint accepts JPEG and PNG — the owner's point that "invoice
// 可以是 pdf，也有可能是照片". The accept list now comes from the field's own allowed formats
// through the shared contract, which is aligned with the backend validator.
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
};

function contentType(file: File): string {
  if (file.type) return file.type.split(';', 1)[0].toLowerCase();
  return MIME_BY_EXTENSION[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? '';
}

export function validateAttachmentFile(
  file: File,
  documentKind: string | undefined,
  maxBytes: number,
): 'type' | 'size' | null {
  const mime = contentType(file);
  const allowed = documentKind === 'invoice'
    ? ['application/pdf']
    : ['application/pdf', 'image/jpeg', 'image/png'];
  if (!allowed.includes(mime)) return 'type';
  if (file.size > Math.min(maxBytes, BACKEND_MAX_BYTES)) return 'size';
  return null;
}

const ProcessAttachmentField: React.FC<Props> = ({
  definitionId, fieldId, label, required, documentKind, accept, allowedTypes, maxBytes = BACKEND_MAX_BYTES,
  value, onChange, onBusyChange, onErrorChange,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const effectiveAccept = accept || attachmentAccept(allowedTypes);
  const effectiveMax = Math.min(maxBytes, BACKEND_MAX_BYTES);

  const reportError = (message: string) => {
    setError(message);
    onErrorChange(true);
  };
  const clearError = () => {
    setError(null);
    onErrorChange(false);
  };

  const choose = async (file: File | undefined) => {
    if (!file) return;
    onChange(undefined);
    const validation = validateAttachmentFile(file, documentKind, effectiveMax);
    if (validation) {
      reportError(t(validation === 'type'
        ? documentKind === 'invoice'
          ? 'worker:submitRequest.invoiceFileTypeError'
          : 'worker:submitRequest.attachmentFileTypeError'
        : 'worker:submitRequest.attachmentFileSizeError', {
          size: Math.round(effectiveMax / 1024 / 1024),
        }));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setUploading(true);
    onBusyChange(true);
    try {
      const intent = await meApi.processAttachments.createIntent({
        definition_id: definitionId,
        field_id: fieldId,
        file_name: safeAttachmentFilename(file.name),
        content_type: contentType(file),
        size_bytes: file.size,
      });
      await meApi.processAttachments.upload(intent, file);
      const staged: StagedProcessAttachment = {
        id: intent.id,
        field_id: intent.field_id,
        file_name: intent.file_name,
        content_type: intent.content_type,
        size_bytes: intent.size_bytes,
        document_kind: intent.document_kind,
        status: intent.status,
      };
      onChange(staged);
      clearError();
    } catch (caught) {
      onChange(undefined);
      reportError(caught instanceof ApiError
        ? translateApiError(caught, t)
        : t('worker:submitRequest.attachmentUploadFailed'));
    } finally {
      setUploading(false);
      onBusyChange(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    if (!value) return;
    setUploading(true);
    onBusyChange(true);
    try {
      await meApi.processAttachments.removeStaged(value.id);
      onChange(undefined);
      clearError();
    } catch (caught) {
      reportError(caught instanceof ApiError
        ? translateApiError(caught, t)
        : t('worker:submitRequest.attachmentRemoveFailed'));
    } finally {
      setUploading(false);
      onBusyChange(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}{required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
        </Typography>
        {value && (
          <Button size="small" color="inherit" startIcon={<CloseOutlined />} onClick={remove}>
            {t('worker:submitRequest.removeAttachment')}
          </Button>
        )}
      </Stack>
      {value ? (
        <Stack spacing={0.25} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{safeAttachmentFilename(value.file_name)}</Typography>
          <Typography variant="caption" color="text.secondary">{formatFileSize(value.size_bytes)}</Typography>
        </Stack>
      ) : (
        <Button variant="outlined" size="small" startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileOutlined />}
          onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? t('worker:submitRequest.uploadingAttachment') : t('worker:submitRequest.chooseAttachment')}
        </Button>
      )}
      <input ref={inputRef} hidden type="file" accept={effectiveAccept}
        aria-label={label} onChange={(event) => choose(event.target.files?.[0])} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {t(documentKind === 'invoice'
          ? 'worker:submitRequest.invoiceAttachmentHint'
          : 'worker:submitRequest.generalAttachmentHint', { size: Math.round(effectiveMax / 1024 / 1024) })}
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
};

export default ProcessAttachmentField;
